/**
 * chatStore — central reactive state for the AI chat UI.
 *
 * Built on @preact/signals. All AG-UI stream events mutate this store;
 * Preact components subscribe automatically via signal reads.
 *
 * Message shape:
 *   { id, type: 'user'|'ai'|'thinking', content, timestamp, isStreaming, cost }
 *
 * Subagent shape (keyed by instanceId or agent name, reset each turn):
 *   { name, instanceId, tools: [{type:'start'|'end'|'text', toolName?, text?}],
 *     currentTool, active, firstSeen }
 *
 * The `tools` array is the temporal timeline of a subagent run:
 *   - 'start' / 'end' entries represent tool calls (merged by SubagentPanel)
 *   - 'text'  entries represent streamed model output between tool calls
 */

import { signal, computed, batch } from '@preact/signals'

const DEV_LOGGING = __DEV_LOGGING__

// ── Message ID counter ──────────────────────────────────────────────────────

let _msgCounter = 0
const nextMsgId = () => `msg-${++_msgCounter}-${Date.now()}`

// ── Throttle helper for text delta writes (one rAF per frame) ───────────────

function makeThrottledWriter(sigRef) {
  let pending = false
  let pendingValue = null
  return (value) => {
    pendingValue = value
    if (!pending) {
      pending = true
      requestAnimationFrame(() => {
        pending = false
        sigRef.value = pendingValue
        pendingValue = null
      })
    }
  }
}

// ── Store factory ────────────────────────────────────────────────────────────

export function createChatStore({ i18n } = {}) {
  // ── Core state signals ─────────────────────────────────────────────────

  const messages       = signal([])          // Message[]
  const streamingId    = signal(null)         // string|null — id of the currently streaming AI message
  const reasoningMap   = signal({})           // { [msgId]: string } accumulated reasoning text
  const status         = signal(null)         // { code, text } | null
  const subagents      = signal({})           // { [name]: SubagentState }
  const costSignal     = signal(null)         // CostInfo | null
  const titleSignal    = signal('')           // conversation title
  const streamingHasContent = signal(false)  // true once first real content arrives (text/reasoning/tool)

  // ── Computed ──────────────────────────────────────────────────────────

  const isActive = computed(() =>
    streamingId.value !== null ||
    Object.values(subagents.value).some(s => s.active)
  )

  // ── Streaming buffer (not a signal — polled by rAF inside store) ───────
  // We keep the raw text buffer here so the AiBubble can read it synchronously.

  let _streamBuffer = ''
  const streamBufferSignal = signal('')  // throttled mirror of _streamBuffer
  const _writeStreamBuffer = makeThrottledWriter(streamBufferSignal)

  // ── Helpers ───────────────────────────────────────────────────────────

  function _getStreamingMsg() {
    const id = streamingId.value
    if (!id) return null
    return messages.value.find(m => m.id === id) || null
  }

  function _updateMessage(id, patch) {
    messages.value = messages.value.map(m => m.id === id ? { ...m, ...patch } : m)
  }

  // ── Mutators ──────────────────────────────────────────────────────────

  /**
   * Called on RUN_STARTED: prepare an empty AI message bubble.
   * The bubble immediately becomes visible (with ActivityIndicator) before
   * any text arrives.
   */
  function runStarted() {
    const id = nextMsgId()
    batch(() => {
      messages.value = [
        ...messages.value,
        { id, type: 'ai', content: '', timestamp: new Date(), isStreaming: true, cost: null }
      ]
      streamingId.value = id
      // streamingHasContent is NOT reset here — it was already reset in addUserMessage()
      // so subagent sub-runs within the same turn don't re-trigger the connecting bar
      _streamBuffer = ''
      streamBufferSignal.value = ''
      // Reset subagents for the new turn so stale panels from previous turns don't linger.
      subagents.value = {}
      // status stays visible (blue bar) until first content arrives
    })
    if (DEV_LOGGING) console.log('[chatStore] runStarted, id:', id)
  }

  /**
   * Called on TEXT_MESSAGE_CONTENT delta.
   * Accumulates into buffer; throttled signal write triggers re-render.
   */
  function appendTextDelta(delta) {
    if (!streamingId.value) {
      // No active bubble yet — create one (safety net)
      runStarted()
    }
    if (!streamingHasContent.value) {
      batch(() => {
        streamingHasContent.value = true
        status.value = null  // hide blue connecting bar — bubble will show
      })
    }
    _streamBuffer += delta
    _writeStreamBuffer(_streamBuffer)
  }

  /**
   * Called on REASONING_START / REASONING_MESSAGE_CONTENT.
   * Reasoning goes into a separate bubble keyed by the current streamingId.
   */
  function appendReasoningDelta(delta) {
    const id = streamingId.value
    if (!id) return
    if (!streamingHasContent.value) {
      batch(() => {
        streamingHasContent.value = true
        status.value = null
      })
    }
    const current = reasoningMap.value[id] || ''
    reasoningMap.value = { ...reasoningMap.value, [id]: current + delta }
  }

  function reasoningStart() {
    const id = streamingId.value || nextMsgId()
    if (!streamingId.value) {
      // New AI message starting with reasoning
      batch(() => {
        messages.value = [
          ...messages.value,
          { id, type: 'ai', content: '', timestamp: new Date(), isStreaming: true, cost: null }
        ]
        streamingId.value = id
      })
    }
    if (!reasoningMap.value[id]) {
      reasoningMap.value = { ...reasoningMap.value, [id]: '' }
    }
  }

  /**
   * Called on TEXT_MESSAGE_END (end_turn): flush the buffer into the message content.
   * Does NOT finalize (artifacts): that happens at RUN_FINISHED.
   */
  function messageStop() {
    const id = streamingId.value
    if (!id) return
    // Flush buffer into content
    _updateMessage(id, { content: _streamBuffer })
    if (DEV_LOGGING) console.log('[chatStore] messageStop, buffer flushed, id:', id)
  }

  /**
   * Called on RUN_FINISHED: finalize streaming, clear active stream.
   */
  function runFinished(conversationId) {
    const id = streamingId.value
    batch(() => {
      if (id) {
        _updateMessage(id, { content: _streamBuffer, isStreaming: false })
      }
      streamingId.value = null
      streamingHasContent.value = false
      _streamBuffer = ''
      streamBufferSignal.value = ''
      // Clear all subagent active states (mark every parallel run as done)
      const updated = {}
      for (const [key, s] of Object.entries(subagents.value)) {
        updated[key] = { ...s, active: false, currentTool: null }
      }
      subagents.value = updated
      // Keep error StatusAlert if a guardrail/error path already set it
      // (defensive: RUN_FINISHED must not wipe an in-flight error state).
      if (status.value?.code !== 'error') {
        status.value = null
      }
    })
    if (DEV_LOGGING) console.log('[chatStore] runFinished, conversationId:', conversationId)
    return id
  }

  /**
   * Called when streaming completes: returns the final content for artifact rendering.
   */
  function getFinalBuffer() {
    return _streamBuffer
  }

  /**
   * Mark streaming done (called after artifact finalization from controller).
   */
  function markFinalized(msgId) {
    _updateMessage(msgId, { isFinalized: true })
  }

  /**
   * Called on RUN_ERROR: finalize the streaming message (stop spinner) without
   * clearing the status so the error alert remains visible.
   */
  function runErrorFinish() {
    const id = streamingId.value
    batch(() => {
      if (id) {
        _updateMessage(id, { content: _streamBuffer, isStreaming: false })
      }
      streamingId.value = null
      streamingHasContent.value = false
      _streamBuffer = ''
      streamBufferSignal.value = ''
      const updated = {}
      for (const [key, s] of Object.entries(subagents.value)) {
        updated[key] = { ...s, active: false, currentTool: null }
      }
      subagents.value = updated
      // status is intentionally NOT cleared — error message stays visible
    })
  }

  /**
   * Add a user message optimistically (before stream starts).
   */
  function addUserMessage({ content, attachments = [], avatarUrl = null, timestamp = null }) {
    const id = nextMsgId()
    batch(() => {
      messages.value = [
        ...messages.value,
        { id, type: 'user', content, attachments, avatarUrl, timestamp: timestamp || new Date() }
      ]
      // Reset content flag for this new user→AI turn so the connecting bar shows
      // until the first AI content arrives
      streamingHasContent.value = false
    })
  }

  /**
   * Status bar update (tool name, processing, etc.)
   */
  function setStatus(code, text) {
    // text=null means "look up translation by code" — do NOT default text to code,
    // otherwise statusLabel returns the raw code without going through i18n.
    status.value = code ? { code, text: text ?? null } : null
  }

  function clearStatus() {
    status.value = null
  }

  /**
   * Cost info from the stream — attaches to the last AI message.
   */
  function setCostInfo(info) {
    const id = streamingId.value
    if (id) {
      _updateMessage(id, { cost: info })
    } else {
      // Attach to last AI message if streaming already finished
      const msgs = messages.value
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].type === 'ai') {
          _updateMessage(msgs[i].id, { cost: info })
          break
        }
      }
    }
    costSignal.value = info
    if (info?.budget_exceeded) {
      // Signal budget exceeded so controller can update its value
      store._budgetExceeded = true
    }
  }

  /**
   * Subagent panel management.
   *
   * Subagent runs are keyed by `instanceId` (a per-invocation uuid emitted by
   * the hostagent), so multiple parallel runs of the SAME subagent (e.g. two
   * concurrent `tooling_scout` calls in one ToolUse block) stay separate
   * cards instead of merging into one.
   *
   * Older hostagent builds may emit events without `instanceId`; in that case
   * we fall back to the agent name as the key (legacy behavior — collapses
   * parallel runs of the same subagent, but never breaks).
   */
  function _subagentKey(agentName, instanceId) {
    return instanceId || agentName
  }

  function _emptySubagent(agentName, instanceId) {
    return {
      name: agentName,
      instanceId: instanceId || null,
      tools: [],        // [{type:'start'|'end'|'text', toolName?, text?}] — temporal order
      currentTool: null,
      active: false,
      firstSeen: Date.now(),
    }
  }

  function agentRunning(name, instanceId = null) {
    const key = _subagentKey(name, instanceId)
    const existing = subagents.value[key]
    subagents.value = {
      ...subagents.value,
      [key]: existing
        ? { ...existing, active: true }
        : { ..._emptySubagent(name, instanceId), active: true },
    }
  }

  function subagentToolStart(agentName, toolName, instanceId = null) {
    const key = _subagentKey(agentName, instanceId)
    const shortName = toolName?.includes('___') ? toolName.split('___').pop() : (toolName || '')
    const existing = subagents.value[key] || _emptySubagent(agentName, instanceId)
    subagents.value = {
      ...subagents.value,
      [key]: {
        ...existing,
        active: true,
        currentTool: shortName,
        tools: [...existing.tools, { type: 'start', toolName: shortName }],
      },
    }
  }

  function subagentToolEnd(agentName, toolName, instanceId = null) {
    const key = _subagentKey(agentName, instanceId)
    const shortName = toolName?.includes('___') ? toolName.split('___').pop() : (toolName || '')
    const existing = subagents.value[key] || _emptySubagent(agentName, instanceId)
    subagents.value = {
      ...subagents.value,
      [key]: {
        ...existing,
        // Keep `active: true` until subagent_done arrives — a tool ending
        // doesn't mean the subagent is finished (it may chain more tools).
        currentTool: null,
        tools: [...existing.tools, { type: 'end', toolName: shortName }],
      },
    }
  }

  function subagentDelta(agentName, text, instanceId = null) {
    const key = _subagentKey(agentName, instanceId)
    const existing = subagents.value[key] || _emptySubagent(agentName, instanceId)
    const tools = existing.tools
    const last = tools[tools.length - 1]
    let newTools
    if (last && last.type === 'text') {
      // Append to the most recent text entry — preserves temporal position in list.
      newTools = tools.map((e, i) =>
        i === tools.length - 1 ? { ...e, text: e.text + text } : e
      )
    } else {
      // Previous entry was a tool or the list is empty — open a new text block.
      newTools = [...tools, { type: 'text', text }]
    }
    subagents.value = {
      ...subagents.value,
      [key]: { ...existing, tools: newTools },
    }
  }

  function subagentDone(agentName, instanceId = null) {
    const key = _subagentKey(agentName, instanceId)
    const existing = subagents.value[key]
    if (!existing) return
    subagents.value = {
      ...subagents.value,
      [key]: { ...existing, active: false, currentTool: null },
    }
  }

  function setTitle(title) {
    if (title) titleSignal.value = title
  }

  /**
   * Hydrate initial state from pre-rendered ERB DOM.
   * Reads [data-message-type] elements from the output container.
   * Must be called before Preact mounts.
   */
  function hydrateFromDOM(container) {
    if (!container) return
    const elements = container.querySelectorAll('[data-message-type]')
    if (!elements.length) return

    const hydrated = []
    elements.forEach(el => {
      const type = el.dataset.messageType  // 'user' | 'ai'
      const id = el.dataset.messageId || nextMsgId()
      const timestamp = el.dataset.timestamp ? new Date(el.dataset.timestamp) : new Date()

      if (type === 'user') {
        const content = el.dataset.messageContent || el.querySelector('[data-message-text]')?.textContent || ''
        const avatarUrl = el.dataset.avatarUrl || null
        hydrated.push({ id, type: 'user', content, attachments: [], avatarUrl, timestamp })
      } else if (type === 'ai') {
        const content = el.dataset.messageContent || ''
        // Parse cost from data attributes if present
        let cost = null
        if (el.dataset.messageCost) {
          try { cost = JSON.parse(el.dataset.messageCost) } catch { /* ignore */ }
        }
        hydrated.push({ id, type: 'ai', content, timestamp, isStreaming: false, isFinalized: false, cost })
      }
    })

    if (hydrated.length) {
      messages.value = hydrated
      if (DEV_LOGGING) console.log('[chatStore] hydrated', hydrated.length, 'messages from DOM')
    }
  }

  /**
   * Hydrate initial state from a plain JSON message list (role/content pairs),
   * e.g. GET /support/widget/ai_conversations/:agent_id — used by the beacon
   * widget's AiScreen when the user reopens/continues a previous conversation,
   * since it has no server-rendered DOM to hydrate from like the full /ai page.
   */
  function hydrateMessages(list) {
    if (!Array.isArray(list) || !list.length) return

    const hydrated = list.map((item) => {
      const timestamp = item.created_at ? new Date(item.created_at) : new Date()
      return item.role === 'user'
        ? { id: nextMsgId(), type: 'user', content: item.content || '', attachments: [], avatarUrl: null, timestamp }
        : { id: nextMsgId(), type: 'ai', content: item.content || '', timestamp, isStreaming: false, isFinalized: true, cost: null }
    })

    messages.value = hydrated
    if (DEV_LOGGING) console.log('[chatStore] hydrated', hydrated.length, 'messages from JSON list')
  }

  const store = {
    // Signals (readable by components)
    messages,
    streamingId,
    streamingHasContent,
    reasoningMap,
    streamBufferSignal,
    status,
    subagents,
    cost: costSignal,
    title: titleSignal,
    isActive,

    // i18n reference (for components)
    i18n,

    // Mutators (called by ChatStreamHandler)
    runStarted,
    appendTextDelta,
    appendReasoningDelta,
    reasoningStart,
    messageStop,
    runFinished,
    runErrorFinish,
    getFinalBuffer,
    markFinalized,
    addUserMessage,
    setStatus,
    clearStatus,
    setCostInfo,
    agentRunning,
    subagentToolStart,
    subagentToolEnd,
    subagentDelta,
    subagentDone,
    setTitle,
    hydrateFromDOM,
    hydrateMessages,

    // Internal flag readable by controller
    _budgetExceeded: false,
  }

  return store
}
